import { ReactComponent as AdeccoWordmark } from '../assets/adecco-wordmark.svg';
import faviconUrl from '../assets/favicon.svg';
import { DEFAULT_BRAND_COLOR } from '../theme';

export type ApplyFlowTenant = {
  /** Used for the wordmark link's accessible name. */
  name: string;
  /**
   * App bar ground and exit-state ground. Never an accent, a selected state, a link
   * colour or an error colour — see `selectedRingCss`.
   */
  brandColor: string;
  /**
   * Rendered white on the brand ground, so the mark's fills must be `currentColor`.
   * A full-colour logo will not survive here; that is why this is a bundled asset
   * rather than the tenant's `logo_url`.
   */
  Wordmark: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /** Where the wordmark links to. Opens in a new tab. */
  websiteUrl: string;
  /**
   * The tab icon while the flow is open, replacing Robin's for the duration.
   *
   * A URL rather than a component, unlike `Wordmark`: this one is set as a `link` href,
   * never rendered, so it carries its own colours instead of inheriting `currentColor`.
   */
  faviconUrl: string;
  /**
   * The tab title while the flow is open.
   *
   * Separate from `name`, which is the wordmark's accessible name and so has to match the
   * mark on screen. This one names the whole proposition — the agency and the employer the
   * candidate is applying to work for — because the tab is often the only thing visible
   * when the page is in the background.
   */
  documentTitle: string;
  /**
   * Where the post-interview CTA sends the candidate: the tenant's own candidate portal.
   *
   * Generic, and the same for every candidate on this flow — it replaced a single job's apply
   * page, which was only ever right for the one vacancy it came from.
   */
  profileUrl: string;
  /**
   * The address the footer offers candidates on this flow.
   *
   * The tenant's own inbox, not Robin's: a candidate with a question about this application
   * needs the agency recruiting for it, and Robin's support desk cannot answer them. Every
   * other `questionsEmailUs` footer in the app still points at `support@recruitrobin.com`,
   * which is correct there — those screens are Robin's own.
   */
  supportEmail: string;
};

/**
 * The apply flow's branding.
 *
 * Fixed to Adecco for now — they are the first tenant on this flow and possibly the
 * only one. The indirection exists so that when branding becomes API-driven (a mono
 * logo variant and a website URL on the screening state endpoint) only this module
 * changes; nothing downstream reads a tenant colour or asset directly.
 */
export const applyFlowTenant: ApplyFlowTenant = {
  name: 'Adecco',
  brandColor: DEFAULT_BRAND_COLOR,
  Wordmark: AdeccoWordmark,
  websiteUrl: 'https://www.adecco.com/nl-nl',
  faviconUrl,
  documentTitle: 'Adecco | Uber Eats',
  profileUrl: 'https://portal.myadecco.nl/',
  supportEmail: 'ubereats@adecco.nl',
};
